/**
 * Settings that exist purely so the EDITOR can do something, and that the
 * reader never reads. `renderStory` serializes the whole story into
 * `window.story` in the published page, so anything left on an entity is
 * readable by every viewer from the browser console.
 *
 * `dataSource` is the linked spreadsheet binding written by charts, tables and
 * counters. It carries the author's `spreadsheetUrl` / OneDrive `webUrl` and
 * `itemId`, the document and worksheet names and the exact cell range — none
 * of which the reader needs (the values themselves are already baked into the
 * element), and all of which describe internal files the story was built from.
 * Sync, "Open source" and "Edit range" all run in the editor against the copy
 * held in Redux / the story record, so stripping it here costs nothing.
 *
 * Keep this list to settings with NO reader-side consumer. Anything the
 * published bundle reads must stay.
 */
const EDITOR_ONLY_SETTINGS = ["dataSource"] as const;

// Deliberately structural rather than typed against StaticEntityType: this runs
// on an immer draft, and grouped table cells / combo layouts nest entities
// arbitrarily deep. Walking defensively also means a schema change can't
// silently stop stripping.
type Node = { settings?: unknown; children?: unknown };

const stripEntity = (entity: unknown): void => {
  if (!entity || typeof entity !== "object") return;
  const node = entity as Node;
  const settings = node.settings;
  if (settings && typeof settings === "object")
    for (const key of EDITOR_ONLY_SETTINGS) {
      if (key in settings) delete (settings as Record<string, unknown>)[key];
    }
  if (Array.isArray(node.children))
    for (const child of node.children) stripEntity(child);
};

/**
 * Remove every editor-only setting from a story's entity tree, in place.
 * Callers pass a draft/clone — never the live Redux story.
 */
export const stripEditorOnlySettings = (story: unknown): void => {
  if (!story || typeof story !== "object") return;
  const slides = (story as { slides?: unknown }).slides;
  if (!Array.isArray(slides)) return;
  for (const slide of slides) stripEntity(slide);
};
